import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { profitabilitySimulatorCalculation } from '../../../lib/utils/profitabilitySimulator'
import useLocale from '../../../hooks/useLocale'
import EstimatedGainsCard from '../../shared/EstimatedGainsCard'

type InvestmentSimulationBentoProps = {
  locked?: boolean
  defaultInitialInvestment?: number
  defaultYearlyYield?: number
  defaultYearlyPriceAppreciation?: number
  defaultTotalPeriod?: number
}

export default function InvestmentSimulationBento({
  locked = false,
  defaultInitialInvestment = 1000,
  defaultYearlyYield = 900,
  defaultYearlyPriceAppreciation = 100,
  defaultTotalPeriod = 5,
}: InvestmentSimulationBentoProps) {
  const { t } = useTranslation('home')
  const { locale } = useLocale()

  const [initialInvestment, setInitialInvestment] = useState<
    number | undefined
  >(defaultInitialInvestment)
  const [yearlyYield, setYearlyYield] = useState<number>(defaultYearlyYield)
  const [yearlyPriceAppreciation, setYearlyPriceAppreciation] =
    useState<number>(defaultYearlyPriceAppreciation)
  const [totalPeriod, setTotalPeriod] = useState<number>(defaultTotalPeriod)

  const [totalProfitability, setTotalProfitability] = useState<number>(0)

  const today = new Date()
  const changeDate = new Date('2026-02-01')
  const LIVRET_A_RATE = today >= changeDate ? 1.5 : 1.7

  const [livretAGains, setLivretAGains] = useState<number>(0)

  const handleInitialInvestmentChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = Number(e.target.value) || undefined
    setInitialInvestment(value)
  }

  const handleYearlyYieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (locked) return
    setYearlyYield(Number(e.target.value))
  }

  const handleYearlyPriceAppreciationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (locked) return
    setYearlyPriceAppreciation(Number(e.target.value))
  }

  const handleTotalPeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (locked) return
    setTotalPeriod(Number(e.target.value))
  }

  useEffect(() => {
    const { totalProfitability } = profitabilitySimulatorCalculation(
      Number(initialInvestment ?? 0),
      Number(yearlyYield),
      Number(yearlyPriceAppreciation),
      Number(totalPeriod),
    )

    setTotalProfitability(totalProfitability)

    const livretA =
      (initialInvestment ?? 0) * (LIVRET_A_RATE / 100) * totalPeriod
    setLivretAGains(livretA)
  }, [
    LIVRET_A_RATE,
    initialInvestment,
    yearlyYield,
    yearlyPriceAppreciation,
    totalPeriod,
  ])

  const lockedInputClass = `h-2 w-full appearance-none rounded-lg border border-neutralBlack/10
    ${locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer bg-gray-200'}`

  return (
    <div className="relative flex h-full w-full flex-col items-center">
      <img
        src="/img/icons/3d/locker-sm-min.png"
        className="absolute -left-10 -top-10 z-10 h-20 w-auto -rotate-6"
        alt="locker icon 3d"
      />

      <img
        src="/img/icons/3d/chart-sm-min.png"
        className="absolute -right-14 top-[30%] z-10 h-36 w-auto rotate-[24deg]"
        alt="chart icon 3d"
      />

      <img
        src="/img/icons/3d/dollar-coin-sm-min.png"
        className="absolute -left-8 bottom-0 z-10 h-28 w-auto -rotate-6"
        alt="dollar icon 3d"
      />

      <div className="z-10">
        <p className="text-center font-medium text-neutralBlack">
          {t('bento.investmentSimulationBento.title')}
        </p>

        <div className="w-full grow px-6 pt-3">
          {/* Montant investissement (toujours actif) */}
          <div className="relative mb-1 w-full">
            <label
              className="mb-1 text-xs font-bold text-neutralBlack/80"
              htmlFor="initialInvestment"
            >
              {t('bento.investmentSimulationBento.sliders.initialInvestment')}
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const newValue = Math.max(0, (initialInvestment ?? 0) - 100)
                  setInitialInvestment(newValue)
                }}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-neutralBlack/10 bg-gray-100 font-bold text-neutralBlack transition-colors hover:bg-gray-200"
              >
                −
              </button>
              <div className="relative flex-1">
                <input
                  id="initialInvestment"
                  type="number"
                  min="0"
                  onWheel={(e) => e.currentTarget.blur()}
                  max="100000"
                  step="10"
                  value={initialInvestment}
                  className="w-full rounded-lg border border-neutralBlack/10 py-2 pl-3 pr-6 text-center text-sm font-medium outline-none focus:border-neutralBlack/30 focus:ring-1 focus:ring-neutralBlack/20"
                  onChange={handleInitialInvestmentChange}
                />
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-bold text-neutralBlack">
                  €
                </span>
              </div>
              <button
                onClick={() => {
                  const newValue = Math.min(
                    100000,
                    (initialInvestment ?? 0) + 100,
                  )
                  setInitialInvestment(newValue)
                }}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-neutralBlack/10 bg-gray-100 font-bold text-neutralBlack transition-colors hover:bg-gray-200"
              >
                +
              </button>
            </div>
          </div>

          {/* Rendement annuel (lockable) */}
          <div className="relative mb-1 w-full">
            <label
              className="text-xs font-bold text-neutralBlack/80"
              htmlFor="yearlyYield"
            >
              {t('bento.investmentSimulationBento.sliders.netRentalYield')}
            </label>
            <input
              id="yearlyYield"
              type="range"
              min="100"
              max="2000"
              step="50"
              defaultValue={yearlyYield}
              disabled={locked}
              className={lockedInputClass}
              onChange={handleYearlyYieldChange}
            />
            <p className="text-center font-medium">
              {Number(yearlyYield) / 100}%
            </p>
          </div>

          {/* Appréciation annuelle (lockable) */}
          {defaultYearlyPriceAppreciation > 0 ? (
            <div className="relative mb-1 w-full">
              <label
                className="text-xs font-bold text-neutralBlack/80"
                htmlFor="yearlyPriceAppreciation"
              >
                {t(
                  'bento.investmentSimulationBento.sliders.yearlyPriceAppreciation',
                )}
              </label>
              <input
                id="yearlyPriceAppreciation"
                type="range"
                min="0"
                max="1000"
                step="50"
                defaultValue={yearlyPriceAppreciation}
                disabled={locked}
                className={lockedInputClass}
                onChange={handleYearlyPriceAppreciationChange}
              />
              <p className="text-center font-medium">
                {Number(yearlyPriceAppreciation) / 100}%
              </p>
            </div>
          ) : null}

          {/* Durée (lockable) */}
          <div className="relative w-full">
            <label
              className="text-xs font-bold text-neutralBlack/80"
              id="totalPeriod"
            >
              {t('bento.investmentSimulationBento.sliders.estimatedIncomeOn')}
            </label>
            <input
              id="totalPeriod"
              type="range"
              min="1"
              max="25"
              step="1"
              defaultValue={totalPeriod}
              disabled={locked}
              className={lockedInputClass}
              onChange={handleTotalPeriodChange}
            />
            <p className="text-center font-medium">
              {t('bento.investmentSimulationBento.years', {
                count: Number(totalPeriod),
              })}
            </p>
          </div>

          {/* Résultats */}
          <EstimatedGainsCard
            className="mb-4 mt-4"
            totalProfitability={totalProfitability}
            totalPeriod={totalPeriod}
            locale={locale}
            livretARate={LIVRET_A_RATE}
            livretAGains={livretAGains}
            showLivretAComparison={true}
          />
        </div>

        <p className="text-center text-xs text-neutralBlack/80">
          {t('bento.investmentSimulationBento.helpText')}
        </p>
      </div>
    </div>
  )
}
